import { BOSSES } from './bosses.js';
import { RadialRing } from '../systems/spawning/patterns/RadialRing.js';
import { HomingShardVolley } from '../systems/spawning/patterns/HomingShardVolley.js';
import { SweepingLaser } from '../systems/spawning/patterns/SweepingLaser.js';
import { BulletBoardGrid } from '../systems/spawning/patterns/BulletBoardGrid.js';
import { Implosion } from '../systems/spawning/patterns/Implosion.js';
import { FalteringLantern } from '../systems/spawning/patterns/FalteringLantern.js';
import { TwinVoice } from '../systems/spawning/patterns/TwinVoice.js';
import { Hushfall } from '../systems/spawning/patterns/Hushfall.js';
import { Swell } from '../systems/spawning/patterns/Swell.js';
import { ChorusOfTheDeep } from '../systems/spawning/patterns/ChorusOfTheDeep.js';
import { FirstSilence } from '../systems/spawning/patterns/FirstSilence.js';

/**
 * Act II — The Undersong (see ACT2-BIBLE.md). Melodia's chord kept the tuneless
 * deep beneath it asleep; the Unmade King removed the chord. These are what
 * rise into the emptied world. Same def shape as data/bosses.js, so the whole
 * Boss / SpawningSystem / renderer pipeline works unchanged.
 *
 * The act ships complete: six bosses, three deep wardens in front of the
 * Undersong, its own leaderboard board, reward tables (ACT2_ECHO_BONUS /
 * ACT2_SHARDS), epilogue and act-select entry. It reaches the player through
 * ActSelect rather than through the campaign — `ACT2_BOSSES` stays out of
 * `BOSSES` because an act is a separate route, not an appended continuation of
 * Act I's gauntlet (see data/acts.js, which composes the two).
 *
 * `gloaming` is a def property read by Game._drawGloaming: the arena renders
 * near-black except a lit disc around the player. Declared by The Gloam, The
 * Undersong and the Gloam-Remnant warden. `visionRadius` (world px) shrinks by
 * `radiusPerPhase` each phase down to `minRadius`; `fadeBand` softens the edge;
 * `darkness` is the outside opacity. Every other boss omits it and renders
 * exactly as before.
 *
 * `layerFromPhase` runs 2, 2, 2, 1, 1, 0 down this array (A9) — the act used
 * to be a flat 2 with the Undersong at 1, which set a difficulty level where
 * Act I sets a slope. See the staircase note in data/bosses.js for why every
 * act now descends to a finale that never gives the player an unlayered phase.
 */
export const ACT2_BOSSES = [
  {
    name: 'The Gloam',
    subtitle: 'Act II · Undersong',
    // Near-black floor, cold-blue accent — the last light in a lightless deep.
    arenaTheme: { floor: '#07080c', line: '#161a26', accent: '#6ea8ff' },
    spriteKey: 'gloam',
    musicTheme: 'gloam',
    gloaming: { visionRadius: 92, radiusPerPhase: 13, minRadius: 52, fadeBand: 26, darkness: 0.95 },

    // The act's HP staircase: 12300 -> 60900, a flat ~1.38x a stage, solved
    // against the seeded bench the same way Act I's was (ACT-PARITY A19; the
    // method is written up in data/bosses.js and is the part worth copying).
    //
    // Two premises died to get here, and both had been load-bearing:
    //
    //  - **"No dip after the King" (ACT2-BIBLE §9).** The Gloam used to open at
    //    18000 because the King ended at 17000 and Act II was an APPENDED
    //    continuation — one run, arriving here at peak power. It isn't one.
    //    `acts.js` ends Act I at `BOSSES.length` and starts this act as its own
    //    route at `actStartLevel` 16, so nothing crosses that boundary and the
    //    two numbers were never fought by the same build. Comparing them was
    //    comparing a level-38 fight to a level-16 one, and it is why this
    //    opener spent so long inflated.
    //  - **Per-act slopes are a design position.** They are not: A6 argued the
    //    ordering 1.30x / 1.14x / 1.18x was intentional (an act's HP should
    //    climb as fast as its player does, and a level-1 start compounds
    //    fastest). The direction was right and every magnitude was wrong. Once
    //    measured, all three acts wanted a slope FAR steeper than any of them
    //    had, and the honest statement is not a slope at all — it is a target
    //    time-to-kill per fight, with the slope falling out of it.
    //
    // Benched here: 28 25 25 24 28 33 seconds a boss, against 40 32 27 23 24 22
    // before — an act that used to get faster as it went now gets slower. It is
    // deliberately tuned to Act III's measured profile rather than to a number
    // of its own: both are six-boss act-select routes, so Act II starting eight
    // levels weaker should reach the same per-fight length on LESS hp, which is
    // exactly what a finale of 60900 against the Anthem's 64000 says.
    // BOSS-REWORK.md §B4/§B8 — the dark keeps its distance and takes its
    // time: you are always looking for it, which is the whole fight.
    movement: 'keepAway',
    movementConfig: { preferredRange: 210 },
    telegraphScale: 1.15,
    cardBreak: { fraction: 0.06, cards: ['Faltering Lantern'] },
    script: [
      ['Faltering Lantern', 'Radial Ring'],
      ['Faltering Lantern', 'Bullet Board'],
    ],
    maxHp: 12300,
    moveSpeed: 40,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.8,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 12, speed: 92 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 13, speed: 100, turnRate: 1.8 } }),
      new SweepingLaser({ minPhase: 1, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.4 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 13, speed: 62 } }),
      new Implosion({ minPhase: 1, config: { count: 16, damage: 12, speed: 130 } }),
      // Signature: the lights go out and a slow ring fires into the dark.
      new FalteringLantern({ minPhase: 2, config: { snuffMult: 0.28, ringCount: 20, ringSpeed: 68, ringDamage: 13 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 56,
      minionMoveSpeed: 92,
      minionContactDamage: 12,
      minionShotDamage: 6,
      minionShotSpeed: 90,
      minionFireInterval: 1.8,
      // The dark's own lure — you fight half-blind here before the Gloam makes it total.
      deepMinionTypes: [{ type: 'anglerling', weight: 3 }],
    },
  },
  {
    name: 'The Antiphon',
    subtitle: 'Act II · Undersong',
    // One card accent (warm); the two combat tones live in `polarity` below.
    arenaTheme: { floor: '#0a0a12', line: '#2a2340', accent: '#ff8a4d' },
    spriteKey: 'antiphon',
    musicTheme: 'antiphon',
    // Signature: player attunement (Player.attunement, flipped by dash) makes
    // one tone pass through and the other bite +mismatchBonus. Read by
    // SpawningSystem collision (per-bullet) and Game._drawAttunement (the
    // player's current-tone ring). Toned bullets are authored by TwinVoice.
    polarity: { warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5 },

    // §B4/§B7 — it mirrors you across the hall, and every card it plays
    // arrives toned, alternating: polarity as a block to route through
    // rather than a coin flip to ignore.
    movement: 'mirror',
    tonedCards: { mismatchBonus: 0.3 },
    cardBreak: { fraction: 0.06, cards: ['Twin Voice'] },
    script: [
      ['Twin Voice', 'Radial Ring'],
      ['Twin Voice', 'Implosion'],
    ],
    maxHp: 17000,
    moveSpeed: 42,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.78,
    createPatterns: () => [
      // Untoned pressure — always hits, so attunement is never a full answer.
      new RadialRing({ minPhase: 0, config: { damage: 12, speed: 96 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 13, speed: 104, turnRate: 1.85 } }),
      new Implosion({ minPhase: 1, config: { count: 16, damage: 12, speed: 135 } }),
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.5 } }),
      // Signature: the two hating voices as one interleaved wall.
      new TwinVoice({ minPhase: 0, config: { ringCount: 14, speed: 88, damage: 12, warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 58,
      minionMoveSpeed: 94,
      minionContactDamage: 12,
      minionShotDamage: 6,
      minionShotSpeed: 92,
      minionFireInterval: 1.75,
      // Tone as a DEALING problem, before the boss makes it a taking one.
      deepMinionTypes: [{ type: 'mirrorShade', weight: 3 }],
    },
  },
  {
    name: 'The Great Hush',
    subtitle: 'Act II · Undersong',
    // Desaturated — the color drains where sound dies.
    arenaTheme: { floor: '#0c0d0d', line: '#232626', accent: '#9fb0a8' },
    spriteKey: 'greatHush',
    musicTheme: 'greatHush',

    // §B4/§B8 — silence closes in slowly. It stalks, and its wind-ups are
    // long, because the thing you're being denied is your answer, not your
    // reaction time.
    movement: 'stalk',
    movementConfig: { standoff: 85 },
    telegraphScale: 1.2,
    cardBreak: { fraction: 0.06, cards: ['Hushfall'] },
    script: [
      ['Radial Ring', 'Hushfall'],
      ['Hushfall', 'Bullet Board'],
    ],
    maxHp: 23300,
    moveSpeed: 40,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.76,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 13, speed: 98 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 14, speed: 106, turnRate: 1.9 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 13, speed: 64 } }),
      new Implosion({ minPhase: 1, config: { count: 17, damage: 13, speed: 138 } }),
      // Signature: zones of silence that mute the player's weapons.
      new Hushfall({ minPhase: 1, config: { zoneCount: 3, zoneRadius: 60, growthPerSec: 8, driftSpeed: 20, totalHushPhase: 3, totalHushDuration: 1.2 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 60,
      minionMoveSpeed: 96,
      minionContactDamage: 13,
      minionShotDamage: 6,
      minionShotSpeed: 94,
      minionFireInterval: 1.7,
      // Small bubbles of silence, before the Hush makes them the arena.
      deepMinionTypes: [{ type: 'muffler', weight: 3 }],
    },
  },
  {
    name: 'The Ballast',
    subtitle: 'Act II · Undersong',
    arenaTheme: { floor: '#0b0910', line: '#241f33', accent: '#b98cff' },
    spriteKey: 'ballast',
    musicTheme: 'ballast',
    // Signature: a gravity well at the boss's heart that bends EVERY projectile
    // in flight toward it — boss bullets and the player's own fire alike (see
    // Game._applyBallast + gravityWell.js). No new pattern needed: a plain
    // RadialRing curves back inward on the well, which IS the "Perigee Ring".
    ballast: { wellStrength: 60, wellRadius: 220, affectsPlayerFire: true, driftRadius: 34, driftSpeed: 0.35, invertPhase: 3 },

    // §B4 — dead weight does not move. Its own def comment always said so.
    movement: 'anchor',
    script: [
      ['Bullet Board', 'Implosion'],
      ['Sweeping Laser', 'Radial Ring'],
    ],
    maxHp: 32100,
    moveSpeed: 34, // heavy, slow — the weight does the work, not the chase
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.74,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 13, speed: 92 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 14, speed: 104, turnRate: 1.8 } }),
      new Implosion({ minPhase: 1, config: { count: 17, damage: 13, speed: 132 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 13, speed: 64 } }),
      // Beams aren't pooled bullets, so they don't bend — a fixed threat that
      // reads cleanly against all the curving fire.
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.5 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 62,
      minionMoveSpeed: 94,
      minionContactDamage: 13,
      minionShotDamage: 7,
      minionShotSpeed: 92,
      minionFireInterval: 1.7,
      // Walking terrain that bends your fire, before the boss bends all of it.
      deepMinionTypes: [{ type: 'sinkweight', weight: 3 }],
    },
  },
  {
    name: 'The Swell',
    subtitle: 'Act II · Undersong',
    arenaTheme: { floor: '#0a0c11', line: '#1f2a33', accent: '#57e0d0' },
    spriteKey: 'swell',
    musicTheme: 'swell',

    // §B4 — the deep gives back what you give it, and it comes for you while
    // it does.
    movement: 'stalk',
    movementConfig: { standoff: 95 },
    cardBreak: { fraction: 0.06, cards: ['Swell'] },
    script: [
      ['Swell', 'Radial Ring'],
      ['Swell', 'Implosion'],
    ],
    maxHp: 44200,
    moveSpeed: 40,
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.72,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 13, speed: 96 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 14, speed: 106, turnRate: 1.9 } }),
      new Implosion({ minPhase: 1, config: { count: 17, damage: 13, speed: 136 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 13, speed: 64 } }),
      // Signature: a volley scaled to how hard you've been hitting the boss.
      new Swell({ minPhase: 0, config: { baseCount: 10, baseSpeed: 90, damage: 13, softThreshold: 2500, maxSwell: 1.8, crescendoPhase: 3 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 62,
      minionMoveSpeed: 96,
      minionContactDamage: 13,
      minionShotDamage: 7,
      minionShotSpeed: 94,
      minionFireInterval: 1.65,
      // The Swell's rule is a feedback loop, not a body, so there's no single
      // creature that echoes it. It gets a mixed deep set instead — by this
      // point in the act the deep's creatures are vocabulary the player
      // already reads. Three of the four, not all four: the sinkweight's slow
      // is the one thing that would compound with the Swell's own rising
      // pressure instead of just adding to it.
      deepMinionTypes: [
        { type: 'anglerling', weight: 1 },
        { type: 'mirrorShade', weight: 1 },
        { type: 'muffler', weight: 1 },
      ],
    },
  },
  {
    name: 'The Undersong',
    subtitle: 'Act II · Finale',
    isFinalBoss: true,
    hasPulseGlow: true,
    spriteKeyPhase2: 'undersongPhase2',
    // Near-total black, one cold-white accent — the first light going out.
    arenaTheme: { floor: '#050507', line: '#1a1526', accent: '#e0e6ff' },
    spriteKey: 'undersong',
    musicTheme: 'undersong',
    // SIGNATURE — Unsinging: the finale takes up all of Act II's textures, one
    // per phase, the way TheUnmaking recapitulated Act I. The two boss-def
    // textures gate by phase (Game reads their `minPhase`); the pattern-based
    // ones (polarity/hush/swell) gate through the normal pattern minPhase.
    gloaming: { visionRadius: 110, radiusPerPhase: 16, minRadius: 54, fadeBand: 28, darkness: 0.94, minPhase: 1 },
    ballast: { wellStrength: 55, wellRadius: 240, affectsPlayerFire: true, minPhase: 2, driftRadius: 42, driftSpeed: 0.42, invertPhase: 3 },
    polarity: { warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5 },

    // Top of the Act II staircase (a step above The Swell).
    // §B4/§B8 — the act's finale mirrors, and reads a half-step faster than
    // everything that led to it.
    movement: 'mirror',
    telegraphScale: 0.9,
    tonedCards: { mismatchBonus: 0.3 },
    cardBreak: { fraction: 0.05, cards: ['Twin Voice', 'Hushfall'] },
    script: [
      ['Twin Voice', 'Hushfall'],
      ['Swell', 'Twin Voice'],
      ['Hushfall', 'Swell'],
    ],
    maxHp: 60900,
    moveSpeed: 44,
    layerFromPhase: 0,
    pickCooldownMultiplier: 0.66,
    createPatterns: () => [
      new RadialRing({ minPhase: 0, config: { damage: 14, speed: 98 } }),
      new HomingShardVolley({ minPhase: 1, config: { damage: 15, speed: 108, turnRate: 1.95 } }),
      new Implosion({ minPhase: 1, config: { count: 18, damage: 14, speed: 140 } }),
      new SweepingLaser({ minPhase: 2, config: { tickDamage: 7, tickInterval: 0.13, width: 7, length: 9999, sweepSpeed: 1.6 } }),
      // The hating textures, layering in by phase — polarity, then hush + swell.
      new TwinVoice({ minPhase: 1, config: { ringCount: 14, speed: 90, damage: 13, warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5 } }),
      new Hushfall({ minPhase: 2, config: { zoneCount: 3, zoneRadius: 58, growthPerSec: 8, driftSpeed: 24, totalHushPhase: 3, totalHushDuration: 1.2 } }),
      new Swell({ minPhase: 2, config: { baseCount: 11, baseSpeed: 92, damage: 13, softThreshold: 2600, maxSwell: 1.8, crescendoPhase: 3 } }),
    ],
    // Phase 2 "true form": the spiral that runs polarity at pressure.
    createPhase2Pattern: () => new ChorusOfTheDeep({ config: { arms: 3, spinSpeed: 1.6, fireInterval: 0.12, speed: 112, damage: 13, warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5 } }),
    // Desperation: survive The First Silence — slow fire in near-total dark,
    // with the well still bending it (gloaming + ballast are both live by now).
    createDesperationPattern: () => new FirstSilence({ config: { ringCount: 18, ringSpeed: 58, ringDamage: 28, aimedShots: 3, aimedSpeed: 118, aimedDamage: 30 } }),
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 13,
      minionHp: 64,
      minionMoveSpeed: 98,
      minionContactDamage: 14,
      minionShotDamage: 7,
      minionShotSpeed: 96,
      minionFireInterval: 1.6,
      // The finale recapitulates in its waves the way its patterns do: all
      // four deep types at once, weighted heavily, so the approach to the
      // Undersong is the whole act's vocabulary arriving together.
      deepMinionTypes: [
        { type: 'anglerling', weight: 2 },
        { type: 'mirrorShade', weight: 2 },
        { type: 'muffler', weight: 2 },
        { type: 'sinkweight', weight: 2 },
      ],
    },
  },
];

/**
 * Act I's bosses followed by Act II's, in roster order. It reads as one
 * sequence because the roster IS one sequence — but no run walks the whole
 * thing any more. ACT2-BIBLE §1's "appended continuation" (Act I falling
 * through the Unmade King into the Undersong as one thirteen-boss campaign) is
 * retired: the King ends Act I, and Act II is entered from act select as a
 * standalone six-boss act. See data/acts.js for the split and what it cost.
 *
 * So this is now a roster slice, not a run: `startIndex`/`endIndex` per act is
 * what actually bounds a march. Endless still doesn't use it at all — it loops
 * Act I's BOSSES only (Game._bossDefForIndex), the "infinite Act I" it has
 * always been.
 */
export const CAMPAIGN_BOSSES = [...BOSSES, ...ACT2_BOSSES];

/**
 * Mirrors createMiniboss in data/bosses.js: a warden is its parent boss with a
 * shorter health bar and exactly one always-pickable telegraphed pattern —
 * `layerFromPhase: 99` so patterns never stack, and no phase-2/desperation, so
 * it stays a bounded gate rather than a second full fight. `createPatterns` is
 * a factory (not a prebuilt instance) for the same reason it is there: a
 * pattern instance is stateful and must not be shared across encounters.
 */
function createDeepWarden(bossDef, { name, maxHp, createPattern, overrides = {} }) {
  return {
    ...bossDef,
    ...overrides,
    name,
    maxHp,
    layerFromPhase: 99,
    pickCooldownMultiplier: 1,
    isMiniboss: true,
    createPatterns: () => [createPattern()],
    createPhase2Pattern: undefined,
    createDesperationPattern: undefined,
    // A warden is not the finale, whatever its parent def said.
    isFinalBoss: false,
    minibosses: undefined,
  };
}

const DEEP_SOURCES = {
  gloam: ACT2_BOSSES.find((b) => b.spriteKey === 'gloam'),
  antiphon: ACT2_BOSSES.find((b) => b.spriteKey === 'antiphon'),
  ballast: ACT2_BOSSES.find((b) => b.spriteKey === 'ballast'),
};

/**
 * The Heralds of the Deep (ACT2-BIBLE §4) — three wardens gating the Undersong,
 * the way Act I's four Heralds gate the Unmade King. Deliberately THREE, not
 * four: the finale is the longest fight in the game and a fourth pair would
 * drag the approach to it. Game's herald-queue math is length-agnostic (it
 * spawns them in pairs and advances by two), so an odd count simply means the
 * last one comes up alone.
 *
 * Each reuses an Act II boss's sprite and one signature-adjacent texture, held
 * STATIC — the reduced, unescalating version of a rule the player already met.
 * That's the point of a warden: it tests what the act taught, it doesn't teach
 * anything new right before the finale does.
 */
// 11500 each, and the ceiling is the Gloam rather than the Undersong: a warden
// cut from a boss must not outweigh that boss (act2Bosses.test.js), and A19 put
// the Gloam at 12300. Sized in TIME like Act I's heralds — 3 x 11500 is 21s
// against the Undersong's 32s, or 0.64x, alongside Act I's 0.68x and Act III's
// 0.63x. An HP ratio would have said something else entirely; see the A8 note
// in data/bosses.js for why that unit is the wrong one.
export const ACT2_MINIBOSSES = [
  createDeepWarden(DEEP_SOURCES.gloam, {
    name: 'Gloam-Remnant',
    maxHp: 11500,
    // Static darkness: a fixed, wider disc that never shrinks (radiusPerPhase 0).
    overrides: { gloaming: { visionRadius: 104, radiusPerPhase: 0, minRadius: 104, fadeBand: 26, darkness: 0.92 } },
    createPattern: () => new RadialRing({ minPhase: 0, config: { damage: 15, speed: 100 } }),
  }),
  createDeepWarden(DEEP_SOURCES.antiphon, {
    name: 'Antiphon-Echo',
    maxHp: 11500,
    // One voice only — so unlike the Antiphon, a single correct attunement
    // really does carry you through the whole wall.
    createPattern: () => new TwinVoice({
      minPhase: 0,
      config: {
        ringCount: 15, speed: 96, damage: 15, singleTone: 'cold',
        warmColor: '#ff8a4d', coldColor: '#4dc9ff', mismatchBonus: 0.5,
      },
    }),
  }),
  createDeepWarden(DEEP_SOURCES.ballast, {
    name: 'Ballast-Shard',
    maxHp: 11500,
    // A static well: it never drifts and never inverts, so the lead you learn
    // in the first ten seconds holds for the whole fight.
    overrides: { ballast: { wellStrength: 52, wellRadius: 210, affectsPlayerFire: true } },
    createPattern: () => new RadialRing({ minPhase: 0, config: { damage: 15, speed: 96 } }),
  }),
];

/**
 * Per-boss run-end rewards for the Act II slots, continuing the Act I curves
 * (ECHO_BONUS_PER_BOSS / SHARDS_PER_BOSS in Currency.js, which end at 200 / 10
 * for the King). Kept here rather than extended onto those arrays because their
 * length/values are pinned by Currency.test.js. Tuning knobs — a real-
 * playerbase economy pass (ARCHIVE.md Phase 5) will revisit them alongside
 * the longer campaign's leaderboard/Echo scaling. Indexed within Act II
 * (0 = The Gloam … 5 = The Undersong).
 */
export const ACT2_ECHO_BONUS = [260, 300, 340, 380, 420, 520];
export const ACT2_SHARDS = [12, 12, 14, 16, 18, 30];

// Attached after the fact rather than inline on the def: ACT2_MINIBOSSES is
// derived FROM ACT2_BOSSES, so referencing it inside the array that defines it
// would be a cycle. CAMPAIGN_BOSSES spreads the same object references, so the
// campaign's Undersong picks this up too.
const UNDERSONG = ACT2_BOSSES.find((b) => b.spriteKey === 'undersong');
UNDERSONG.minibosses = ACT2_MINIBOSSES;
// Which shared Archive entry a downed warden unlocks (see data/lore.js) —
// Act I's heralds use the default 'heralds'.
UNDERSONG.minibossLoreKey = 'deepWardens';
